import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { NEW_UPDATES_COUNT } from '@/components/FeatureAnnouncement';

interface AnnouncementContextType {
  showAnnouncement: boolean;
  dismissAnnouncement: () => void;
  hasSeenAnnouncement: boolean;
  newCount: number;
}

const AnnouncementContext = createContext<AnnouncementContextType | undefined>(undefined);

const ANNOUNCEMENT_STORAGE_KEY = 'dombase_feature_announcement_seen';
const ANNOUNCEMENT_VERSION = '1.3'; // Increment when you want to show announcement again

interface AnnouncementProviderProps {
  children: ReactNode;
}

export const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children }) => {
  const [showAnnouncement, setShowAnnouncement] = useState<boolean>(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.version !== ANNOUNCEMENT_VERSION) {
          setShowAnnouncement(true);
        }
      } else {
        // Delay 3s on first visit so the page renders before the modal appears
        setTimeout(() => setShowAnnouncement(true), 3000);
      }
    } catch {
      setShowAnnouncement(true);
    }
  }, []);

  const dismissAnnouncement = () => {
    try {
      localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify({
        version: ANNOUNCEMENT_VERSION,
        dismissedAt: Date.now()
      }));
      setShowAnnouncement(false);
    } catch (error) {
      console.error('Error saving announcement dismissal:', error);
    }
  };

  const hasSeenAnnouncement = !showAnnouncement;
  const newCount = showAnnouncement ? NEW_UPDATES_COUNT : 0;

  const value: AnnouncementContextType = {
    showAnnouncement,
    dismissAnnouncement,
    hasSeenAnnouncement,
    newCount,
  };

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncement = (): AnnouncementContextType => {
  const context = useContext(AnnouncementContext);
  if (context === undefined) {
    throw new Error('useAnnouncement must be used within an AnnouncementProvider');
  }
  return context;
};

